/**
 * Authentication Context using Supabase
 * 
 * Provides authentication state and methods throughout the app.
 * Automatically handles session persistence and refresh.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string, 
    password: string,
    options?: {
      data?: {
        is_recruiter?: boolean;
        [key: string]: any;
      };
    }
  ) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  resetPasswordForEmail: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Prevent concurrent refresh attempts (avoid infinite loops)
  const isRefreshingRef = React.useRef(false);
  const refreshPromiseRef = React.useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    // PKCE flow: exchange code from URL if present (email confirmation links)
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const init = code
      ? supabase.auth.exchangeCodeForSession(code).then(() => {
          window.history.replaceState({}, '', window.location.pathname);
          return supabase.auth.getSession();
        })
      : supabase.auth.getSession();

    init.then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string, 
    password: string,
    options?: {
      data?: {
        is_recruiter?: boolean;
        [key: string]: any;
      };
    }
  ) => {
    // Get current origin for redirect
    // Hardcoded production URL to avoid localhost in email confirmation links
    const redirectUrl = process.env.NODE_ENV === 'production'
      ? 'https://cevee.eu/auth/callback'
      : `${window.location.origin}/auth/callback`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: options?.data || {},
        emailRedirectTo: redirectUrl, // Auto-login after email confirmation
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    try {
      // Try global logout first (invalidates token everywhere)
      await supabase.auth.signOut({ scope: 'global' });
      // Success - clear local state
      setSession(null);
      setUser(null);
    } catch (error) {
      console.warn('Global signOut failed (likely expired token), falling back to local:', error);
      // Fallback to local logout if global fails (e.g. 403 from expired token)
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (localError) {
        console.error('Local signOut also failed:', localError);
      }
      // Always clear local state regardless of errors
      setSession(null);
      setUser(null);
    }
  };

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    // If already refreshing, wait for that to complete (prevent concurrent refreshes)
    if (isRefreshingRef.current && refreshPromiseRef.current) {
      console.debug('🔄 Auth: Waiting for ongoing refresh...');
      return refreshPromiseRef.current;
    }

    // Try cached session first (fast path)
    const { data: { session: cachedSession } } = await supabase.auth.getSession();

    // If we have a valid-looking session, use it
    if (cachedSession?.access_token) {
      const expiresAt = cachedSession.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const needsRefresh = expiresAt && (expiresAt - now) < 60;

      if (!needsRefresh) {
        return cachedSession.access_token;
      }
      console.debug('🔄 Auth: Token expiring soon, refreshing...');
    }

    isRefreshingRef.current = true;
    refreshPromiseRef.current = (async () => {
      try {
        console.debug('🔄 Auth: Refreshing session...');
        const { data, error } = await supabase.auth.refreshSession();

        if (error || !data.session) {
          console.warn('⚠️ Auth: Session refresh failed:', error?.message || 'No session');
          setSession(null);
          setUser(null);
          return null;
        }

        console.debug('✅ Auth: Session refreshed successfully');
        return data.session.access_token;
      } catch (err) {
        console.error('❌ Auth: Refresh error:', err);
        setSession(null);
        setUser(null);
        return null;
      } finally {
        isRefreshingRef.current = false;
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, []);

  const resetPasswordForEmail = async (email: string) => {
    // Get redirect URL for password reset
    const redirectUrl = process.env.NODE_ENV === 'production'
      ? 'https://cevee.eu/reset-password'
      : `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getAccessToken,
    resetPasswordForEmail,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}



